import { DQ9Rand } from '@/DQ9Rand'
import { TMapBasicData } from '@/tmapBasicInfo/TMapBasicData'
import { TBOX_CONTENTS_LIST, TBOX_RANK_RANGES } from '@/constants/TBoxConstants'

/**
 * 宝箱のランクを決定し、中身を算出するクラス
 * @class TBoxItemLogic
 */
class TBoxItemLogic {
  /**
   * @type {DQ9Rand} ドラクエ9基本乱数インスタンス
   */
  private static _dq9rand: DQ9Rand = new DQ9Rand()

  /**
   * 宝箱のランクを決定する
   * @param {TMapBasicData} basicData 宝の地図基本データ
   */
  public static calculateTBoxRank(basicData: TMapBasicData): void {
    const treasureBoxCountList = basicData.treasureBoxCountList
    for (let i = 3; i <= basicData.mapFloorCount; i++) {
      this._dq9rand.seed = basicData.mapSeed + i
      const floorMapData = basicData.floorMapDataList[i - 1]
      const tBoxCoordinates = floorMapData.treasureBoxCoordinates
      const tBoxCount = tBoxCoordinates.length
      // 各階層の宝箱の数の2倍だけ乱数ルーチンを回す
      for (let j = 0; j < tBoxCount * 2; j++) {
        this._dq9rand.generateRandom()
      }
      // 各階層の宝箱のランクを決定
      for (let j = 0; j < tBoxCount; j++) {
        let value = floorMapData.getFloorMapValue(tBoxCoordinates[j].x, tBoxCoordinates[j].y)
        value +=
          this.getRandom(
            TBOX_RANK_RANGES[floorMapData.enemyRank - 1].minTreasureBoxRank,
            TBOX_RANK_RANGES[floorMapData.enemyRank - 1].maxTreasureBoxRank
          ) - 1
        floorMapData.setFloorMapValue(tBoxCoordinates[j].x, tBoxCoordinates[j].y, value)
        treasureBoxCountList[value % 10]++
        treasureBoxCountList[10]++
      }
      basicData.treasureBoxCountList = treasureBoxCountList
    }
  }

  /**
   * 宝箱の中身を算出する
   * @param basicData 宝の地図基本データ
   * @param floor 階数
   * @param boxIndex 宝箱のインデックス
   * @param second 秒数
   * @returns アイテム名
   */
  public static calculateTBoxContents(
    basicData: TMapBasicData,
    floor: number,
    boxIndex: number,
    second: number
  ): string {
    if (floor < 1 || basicData.mapFloorCount < floor) {
      return '取得失敗'
    }
    this._dq9rand.seed = basicData.mapSeed + floor + second - 5
    const floorMapData = basicData.floorMapDataList[floor - 1]
    const tBoxCoordinates = floorMapData.treasureBoxCoordinates
    const tBoxCount = tBoxCoordinates.length
    if (boxIndex < 0 || tBoxCount <= boxIndex) {
      return '取得失敗'
    }
    for (let i = 0; i < tBoxCount; i++) {
      const rand = this.getRandom(0, 99)
      if (i != boxIndex) {
        continue
      }
      const tBoxValue = floorMapData.getFloorMapValue(tBoxCoordinates[i].x, tBoxCoordinates[i].y)
      const tBoxTable = TBOX_CONTENTS_LIST[tBoxValue % 10]
      let sum = 0
      for (let j = 0; j < tBoxTable.length; j++) {
        sum += tBoxTable[j].probability
        if (rand < sum) {
          return tBoxTable[j].name
        }
      }
    }
    return ''
  }

  /**
   * 宝箱のランクを取得する
   * @param basicData 宝の地図基本データ
   * @param floor 階数
   * @param boxIndex 宝箱のインデックス
   * @returns 宝箱のランク
   */
  public static getTBoxRank(basicData: TMapBasicData, floor: number, boxIndex: number): number {
    if (floor < 1 || basicData.mapFloorCount < floor) {
      return -1
    }
    const floorMapData = basicData.floorMapDataList[floor - 1]
    const tBoxCoordinates = floorMapData.treasureBoxCoordinates
    const tBoxCount = tBoxCoordinates.length
    if (boxIndex < 0 || tBoxCount <= boxIndex) {
      return -1
    }
    const tBoxValue = floorMapData.getFloorMapValue(tBoxCoordinates[boxIndex].x, tBoxCoordinates[boxIndex].y)
    return tBoxValue % 10
  }

  /**
   * 最小値・最大値の範囲からランダムな値を返す
   * @param min 最小値
   * @param max 最大値
   * @returns 基本乱数により算出された値
   */
  public static getRandom(min: number, max: number): number {
    const candidates = max - min + 1
    const rand = this._dq9rand.generateRandom()
    return min + ((((rand - 1) * candidates) / 0x7fff) | 0)
  }
}

export { TBoxItemLogic }
